/**
 * Free trial-piece gift: claim it, keep it at one, withdraw it when it no
 * longer applies.
 *
 * All of the eligibility reasoning lives in snippets/cart-trial-offer.liquid.
 * That snippet renders a hidden state node describing what the cart currently
 * holds; this file only reads that description and, when the cart disagrees
 * with the rule, corrects it. Nothing here re-derives SKU logic, so the two
 * halves can never drift apart the way three copies of the tier maths once did.
 *
 * State node contract (snippets/cart-trial-offer.liquid):
 *   data-blocked    "true" when a blocking pack SKU is in the bag
 *   data-gift-qty   gift pieces currently in the bag, summed across every gift line
 *   data-gift-keys  every gift line's key, comma-joined — a size dropdown means
 *                   a shopper who claims twice in different sizes has two real
 *                   gift lines, both of which need correcting
 *   data-qualifying paid (non-gift) units currently in the bag
 *   data-min-items  paid units required to keep the gift
 *   data-max        the ceiling, always 1 today
 *
 * Why line key rather than variant id: a variant can occupy several lines when
 * line item properties differ, and a numeric id addressed to /cart/change.js or
 * /cart/update.js silently targets a line index instead. The key is unambiguous.
 *
 * Why there can be more than one state node: the offer snippet renders once
 * in the drawer (header-actions.liquid) and again on /cart
 * (blocks/_cart-products.liquid) — both derive from the same cart, so they
 * normally agree, but a partial section morph can leave one stale relative
 * to the other. readState() below merges every node conservatively rather
 * than trusting whichever one document.querySelector happens to find first.
 */
(() => {
  if (window.__cartTrialInit) return;
  window.__cartTrialInit = true;

  /** A runaway render -> correct -> render loop would hammer the cart API. */
  const MAX_CORRECTIONS = 4;

  let inFlight = false;
  let corrections = 0;
  let scheduled = null;

  const stateNodes = () => Array.from(document.querySelectorAll('[data-cart-trial-state]'));

  /**
   * Merge every state node's reading conservatively:
   *   blocked    true if ANY node says so — never miss a block because the
   *              other node hasn't caught up yet
   *   qty / keys taken together from whichever node reports the LARGEST
   *              gift quantity — the most complete view of what is actually
   *              in the cart, so a stale "0" elsewhere can't hide a gift
   *              that needs correcting
   *   qualifying the SMALLEST reported count — never assume more paid items
   *              are in the bag than the most cautious node shows
   *   max / minItems are merchant settings, identical on every node
   * @returns {{blocked: boolean, qty: number, keys: string[], qualifying: number, max: number, minItems: number} | null}
   */
  function readState() {
    const nodes = stateNodes();
    if (!nodes.length) return null;

    let blocked = false;
    let qty = 0;
    let keys = [];
    let qualifying = Infinity;

    for (const node of nodes) {
      if (node.dataset.blocked === 'true') blocked = true;

      const nodeQty = Number(node.dataset.giftQty) || 0;
      if (nodeQty > qty) {
        qty = nodeQty;
        keys = (node.dataset.giftKeys || '').split(',').filter(Boolean);
      }

      qualifying = Math.min(qualifying, Number(node.dataset.qualifying) || 0);
    }

    return {
      blocked,
      qty,
      keys,
      qualifying: qualifying === Infinity ? 0 : qualifying,
      max: Number(nodes[0].dataset.max) || 1,
      minItems: Number(nodes[0].dataset.minItems) || 0,
    };
  }

  const sectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

  /**
   * Tell the rest of the theme the cart moved. component-cart-items.js listens
   * for this and morphs its section, which re-renders our state node too.
   */
  function broadcast(data, source) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          sourceId: source,
          data: {
            itemCount: data.item_count,
            sections: data.sections || {},
            source,
          },
        },
      })
    );
  }

  async function postJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data) {
      throw new Error(`cart-trial: ${url} responded ${response.status}`);
    }
    return data;
  }

  /**
   * Mirrors the array form proven by the cart upsell in header-actions.liquid.
   *
   * The `_free_gift` property (leading underscore hides it from the cart UI
   * and order confirmation) is what lets cart-trial-offer.liquid tell this
   * line apart from a shopper who is genuinely buying the same product. Without
   * it, a paid purchase merges into the same line as the gift and the two
   * become indistinguishable.
   */
  function addLine(variantId) {
    const body = { id: Number(variantId), quantity: 1, properties: { _free_gift: 'true' } };
    const ids = sectionIds();
    if (ids.length) body.sections = ids;
    return postJSON('/cart/add.js', body);
  }

  /**
   * Sets several lines' quantities in one request, so two gift lines (two
   * sizes claimed) correct together instead of the second one surviving a
   * one-line-at-a-time /cart/change.js call.
   * @param {Record<string, number>} updates - line key -> target quantity
   */
  function applyUpdates(updates) {
    const body = { updates };
    const ids = sectionIds();
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname;
    }
    return postJSON('/cart/update.js', body);
  }

  /**
   * Bring the cart back in line with the rule, if it has drifted.
   * Returns quietly when there is nothing to do — which is the common case.
   */
  async function reconcile() {
    if (inFlight) return;

    const state = readState();
    if (!state) return;

    const { blocked, qty, keys, qualifying, max, minItems } = state;

    if (!keys.length || qty === 0) {
      corrections = 0;
      return;
    }

    /** @type {Record<string, number> | null} */
    let updates = null;
    if (blocked) {
      // They bought the full pack, so the trial piece is no longer a gift.
      updates = Object.fromEntries(keys.map((key) => [key, 0]));
    } else if (qualifying < minItems) {
      // The claim was earned against paid items that are no longer in the
      // bag — e.g. claimed at 2, then one paid item removed. The gift alone
      // must never be what keeps the offer looking "earned".
      updates = Object.fromEntries(keys.map((key) => [key, 0]));
    } else if (qty > max || keys.length > 1) {
      // Over the ceiling, or claimed in more than one size: keep exactly one
      // gift line at the ceiling and zero every other gift line.
      updates = Object.fromEntries(keys.map((key, i) => [key, i === 0 ? max : 0]));
    }

    if (updates === null) {
      corrections = 0;
      return;
    }

    if (corrections >= MAX_CORRECTIONS) {
      console.warn('cart-trial: giving up after %d corrections', corrections);
      return;
    }

    corrections += 1;
    inFlight = true;
    try {
      const data = await applyUpdates(updates);
      broadcast(data, 'cart-trial-reconcile');
    } catch (error) {
      console.warn('cart-trial: could not correct the gift line(s)', error);
    } finally {
      inFlight = false;
    }
  }

  /** Cart events arrive in bursts; collapse them into one pass. */
  function scheduleReconcile() {
    if (scheduled) clearTimeout(scheduled);
    scheduled = setTimeout(() => {
      scheduled = null;
      reconcile();
    }, 60);
  }

  // ── Claim button ────────────────────────────────────────────────────────
  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-cart-trial-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const original = button.textContent;
    button.disabled = true;
    button.textContent = '…';

    try {
      const data = await addLine(variantId);
      // The row is about to be re-rendered away by the morph; leave the tick
      // visible until then so the click does not feel like it did nothing.
      button.textContent = '✓';
      corrections = 0;
      broadcast(data, 'cart-trial-claim');
    } catch (error) {
      console.warn('cart-trial: could not add the gift', error);
      button.textContent = original;
      button.disabled = false;
    }
  });

  // ── Size selector ───────────────────────────────────────────────────────
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.matches('[data-cart-trial-size]')) return;

    const button = target.closest('.ct')?.querySelector('[data-cart-trial-add]');
    if (button instanceof HTMLElement) button.dataset.variantId = target.value;
  });

  // ── Keep the cart honest ────────────────────────────────────────────────
  document.addEventListener('cart:update', scheduleReconcile);

  /**
   * Bring this feature's state fully in line with the rule right now,
   * bypassing scheduleReconcile()'s 60ms debounce. Registered into the
   * shared checkout guard below (see assets/cart-milestone-rewards.js,
   * which owns the guard's one checkout-button listener) so a shopper who
   * fires checkout before the debounce timer would have run never reaches
   * checkout with a gift line the cart no longer qualifies for.
   */
  async function flush() {
    if (scheduled) {
      clearTimeout(scheduled);
      scheduled = null;
    }
    await reconcile();
    if (scheduled) {
      clearTimeout(scheduled);
      scheduled = null;
    }
    await reconcile();
  }

  // ── Checkout guard ───────────────────────────────────────────────────────
  // Shared with assets/cart-milestone-rewards.js via the same
  // window-flag idiom as window.__cartTrialInit above: whichever of the two
  // loads first installs the one checkout-button listener (each is only
  // loaded per its own settings toggle, so neither can assume the other
  // ran), and every reconcile-based offer just registers a flush() here.
  if (!window.__cartCheckoutGuardFlushers) window.__cartCheckoutGuardFlushers = [];
  window.__cartCheckoutGuardFlushers.push(flush);

  if (!window.__cartCheckoutGuardInit) {
    window.__cartCheckoutGuardInit = true;

    let quantityChangePending = false;
    let quantitySettleTimer = null;

    // component-cart-items.js debounces quantity-selector:update 300ms
    // before it even starts the /cart/change.js request, so a checkout
    // click can beat the request out of the gate entirely. Treat the cart
    // as busy from the moment the shopper moves the stepper until
    // cart:update confirms the resulting request landed — with a generous
    // fallback timeout so a request that never lands (offline, blocked)
    // can never wedge checkout shut.
    document.addEventListener('quantity-selector:update', () => {
      quantityChangePending = true;
      if (quantitySettleTimer) clearTimeout(quantitySettleTimer);
      quantitySettleTimer = setTimeout(() => {
        quantityChangePending = false;
      }, 2000);
    });
    document.addEventListener('cart:update', () => {
      quantityChangePending = false;
      if (quantitySettleTimer) clearTimeout(quantitySettleTimer);
    });

    const cartBusy = () =>
      quantityChangePending || !!document.querySelector('cart-items-component.cart-items-disabled');

    let resubmitting = false;

    // Capture phase, ahead of every bubble-phase checkout listener (the
    // cart-discount.js redirect included) and native form submission, so
    // this is the first thing to see the click.
    document.addEventListener(
      'click',
      (event) => {
        if (resubmitting || event.defaultPrevented) return;

        const target = event.target;
        if (!(target instanceof Element)) return;

        const button = target.closest('#checkout, [name="checkout"]');
        if (!(button instanceof HTMLElement)) return;
        if (button instanceof HTMLButtonElement && button.disabled) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        (async () => {
          const originalDisabled = button instanceof HTMLButtonElement ? button.disabled : false;
          if (button instanceof HTMLButtonElement) button.disabled = true;

          for (let waits = 0; waits < 60 && cartBusy(); waits++) {
            await new Promise((resolve) => setTimeout(resolve, 50));
          }

          for (const flushOne of window.__cartCheckoutGuardFlushers) {
            try {
              await flushOne();
            } catch (error) {
              console.warn('cart-checkout-guard: a flush failed', error);
            }
          }

          if (button instanceof HTMLButtonElement) button.disabled = originalDisabled;
          resubmitting = true;
          button.click();
          resubmitting = false;
        })();
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile, { once: true });
  } else {
    reconcile();
  }
})();
